import Image from 'next/image';
import { oneLine } from 'common-tags';

import { RichInline } from '@/atoms';
import { Prose } from '@/molecules';

import type { FunctionComponent } from 'react';
import type { TypePasoProps } from './types';

/**
 * Paso — item del bloque `Pasos` (proceso "¿Cómo funciona…?").
 * Badge circular (56px, tinte del color de marca) con el número de paso —o un
 * icono opcional dentro— + título + descripción, apilado y centrado dentro de
 * una card blanca (rounded-card + shadow + p-8), según Figma 2182:6650. El grid
 * del bloque la posiciona y `h-full` iguala las alturas de la fila.
 */
export const Paso: FunctionComponent<TypePasoProps> = ({
  className = '',
  icon,
  number,
  title,
  description,
  titleStyleClass,
  descriptionStyleClass,
}: TypePasoProps) => (
  <div
    className={oneLine`bg-neutrals-0 shadow-light-s rounded-card flex h-full flex-col items-center gap-4 p-8 text-center ${className}`}
  >
    {(icon?.src || typeof number === 'number') && (
      <div className="text-primary-500 flex size-14 shrink-0 items-center justify-center rounded-full bg-[rgb(var(--tw-color-primary-500)/0.1)]">
        {icon?.src ? (
          <div className="relative size-7">
            <Image
              src={icon.src}
              alt={icon.alt ?? title ?? ''}
              fill
              sizes="28px"
              className="object-contain"
            />
          </div>
        ) : (
          <span className="font-primary text-[22px] leading-none font-bold">
            {number}
          </span>
        )}
      </div>
    )}

    {title && (
      <RichInline
        as="h3"
        html={title}
        className={oneLine`text-headline text-text-primary font-primary ${
          titleStyleClass ?? 'text-[18px] leading-7 font-bold'
        }`}
      />
    )}

    {description && (
      <Prose
        className={oneLine`text-body text-text-primary ${
          descriptionStyleClass ?? '[&_p]:text-body-s'
        }`}
        html={description}
      />
    )}
  </div>
);

Paso.displayName = 'Paso';
